<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    default: '',
  },
  accept: {
    type: String,
    default: '*/*',
  },
  maxSize: {
    type: Number,
    default: 10 * 1024 * 1024, // 10MB par défaut
  },
});

const emit = defineEmits(['update:modelValue']);

const isDragging = ref(false);
const fileInput = ref(null);

// Liste des fichiers
const files = computed({
  get: () => props.modelValue || [],
  set: value => emit('update:modelValue', value),
});

// Formater la taille du fichier
const formatFileSize = bytes => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};

// Normaliser le fichier (gérer File objects et fichiers Drive)
const normalizeFile = file => {
  if (file instanceof File) {
    return {
      name: file.name,
      size: file.size,
      type: file.type,
      isNew: true,
      originalFile: file,
    };
  }
  // Fichier depuis le Drive (avec ID et URL)
  return {
    id: file.id,
    name: file.name || 'Unknown',
    size: file.size || 0,
    type: file.type || '',
    url: file.url,
    thumbnailUrl: file.thumbnail_url || file.thumbnailUrl,
    uploadedAt: file.uploaded_at || file.uploadedAt,
    isNew: false,
  };
};

// Obtenir l'icône selon le type de fichier
const getFileIcon = file => {
  const normalized = normalizeFile(file);
  const type = normalized.type || '';

  if (type.startsWith('image/')) return 'i-lucide-image';
  if (type.startsWith('video/')) return 'i-lucide-video';
  if (type.startsWith('audio/')) return 'i-lucide-audio-lines';
  if (type.includes('pdf')) return 'i-lucide-file-text';
  if (type.includes('word') || type.includes('document'))
    return 'i-lucide-file-text';
  if (type.includes('excel') || type.includes('spreadsheet'))
    return 'i-lucide-table';
  if (type.includes('powerpoint') || type.includes('presentation'))
    return 'i-lucide-presentation';
  if (
    type.includes('zip') ||
    type.includes('rar') ||
    type.includes('compressed')
  )
    return 'i-lucide-archive';

  return 'i-lucide-file';
};

// Obtenir la couleur selon le type
const getFileColor = file => {
  const normalized = normalizeFile(file);
  const type = normalized.type || '';

  if (type.startsWith('image/')) return 'text-blue-500';
  if (type.startsWith('video/')) return 'text-purple-500';
  if (type.startsWith('audio/')) return 'text-green-500';
  if (type.includes('pdf')) return 'text-red-500';
  if (type.includes('word') || type.includes('document'))
    return 'text-blue-600';
  if (type.includes('excel') || type.includes('spreadsheet'))
    return 'text-green-600';
  if (type.includes('zip') || type.includes('compressed'))
    return 'text-yellow-600';

  return 'text-[#6B7280] dark:text-[#9CA3AF]';
};

// Télécharger un fichier sauvegardé
const downloadFile = file => {
  const normalized = normalizeFile(file);
  if (normalized.url) {
    // Ouvrir dans un nouvel onglet (ActiveStorage gère automatiquement le téléchargement)
    window.open(normalized.url, '_blank');
  }
};

// Ajouter des fichiers
const addFiles = fileList => {
  const newFiles = Array.from(fileList).filter(
    file => file.size <= props.maxSize
  );

  files.value = [...files.value, ...newFiles];
};

// Gérer le drop
const handleDrop = event => {
  isDragging.value = false;
  const droppedFiles = event.dataTransfer.files;
  if (droppedFiles.length) {
    addFiles(droppedFiles);
  }
};

// Gérer la sélection via input
const handleFileSelect = event => {
  const selectedFiles = event.target.files;
  if (selectedFiles.length) {
    addFiles(selectedFiles);
  }
  // Reset input
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

// Supprimer un fichier
const removeFile = index => {
  files.value = files.value.filter((_, i) => i !== index);
};

// Ouvrir le sélecteur de fichiers
const openFileSelector = () => {
  fileInput.value?.click();
};

// Drag & Drop handlers
const handleDragEnter = () => {
  isDragging.value = true;
};

const handleDragLeave = event => {
  // Ne désactiver que si on quitte vraiment la zone
  if (event.target.classList.contains('drop-zone')) {
    isDragging.value = false;
  }
};

const handleDragOver = event => {
  event.preventDefault();
};
</script>

<template>
  <div>
    <label
      v-if="label"
      class="block text-sm font-medium text-[#1D1D1F] dark:text-white mb-1.5"
    >
      {{ label }}
    </label>

    <!-- Zone de drop -->
    <div
      class="drop-zone relative border-2 border-dashed rounded-lg transition-all duration-200"
      :class="{
        'border-[#3860F9] dark:border-[#D3FD58] bg-[#3860F9]/5 dark:bg-[#D3FD58]/5':
          isDragging,
        'border-[#E5E7EB] dark:border-[#2A2A2C] hover:border-[#3860F9]/40 dark:hover:border-[#D3FD58]/40 bg-[#F9FAFB] dark:bg-[#1D1D1F]':
          !isDragging,
      }"
      @drop.prevent="handleDrop"
      @dragover.prevent="handleDragOver"
      @dragenter="handleDragEnter"
      @dragleave="handleDragLeave"
    >
      <div class="p-6 text-center">
        <div
          class="mx-auto size-12 rounded-full flex items-center justify-center mb-3"
          :class="{
            'bg-[#3860F9]/10 dark:bg-[#D3FD58]/10 text-[#3860F9] dark:text-[#D3FD58]':
              isDragging,
            'bg-[#F9FAFB] dark:bg-[#1D1D1F] text-[#6B7280] dark:text-[#9CA3AF]':
              !isDragging,
          }"
        >
          <span class="i-lucide-upload size-6" />
        </div>
        <p class="text-sm font-medium text-[#1D1D1F] dark:text-white mb-1">
          {{ $t('TASKS.FILE_UPLOAD.DRAG_DROP') }}
        </p>
        <p class="text-xs text-[#6B7280] dark:text-[#9CA3AF] mb-3">
          {{ $t('TASKS.FILE_UPLOAD.OR') }}
        </p>
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium rounded-lg bg-[#3860F9] dark:bg-[#D3FD58] text-white dark:text-[#1D1D1F] hover:bg-[#3860F9]/90 dark:hover:bg-[#D3FD58]/90 transition-colors"
          @click="openFileSelector"
        >
          {{ $t('TASKS.FILE_UPLOAD.BROWSE') }}
        </button>
        <p class="text-xs text-[#6B7280] dark:text-[#9CA3AF] mt-3">
          {{
            $t('TASKS.FILE_UPLOAD.MAX_SIZE', { size: formatFileSize(maxSize) })
          }}
        </p>
      </div>

      <!-- Input caché -->
      <input
        ref="fileInput"
        type="file"
        multiple
        :accept="accept"
        class="hidden"
        @change="handleFileSelect"
      />
    </div>

    <!-- Liste des fichiers -->
    <div v-if="files.length > 0" class="mt-3 space-y-2">
      <div
        v-for="(file, index) in files"
        :key="index"
        class="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-[#1D1D1F] border border-[#E5E7EB] dark:border-[#2A2A2C] hover:border-[#3860F9]/40 dark:hover:border-[#D3FD58]/40 transition-colors group"
      >
        <!-- Icône ou miniature du fichier -->
        <div
          class="size-10 rounded-lg flex items-center justify-center bg-[#F9FAFB] dark:bg-[#0F0F10] flex-shrink-0 overflow-hidden"
          :class="getFileColor(file)"
        >
          <!-- Miniature pour les images -->
          <img
            v-if="normalizeFile(file).thumbnailUrl"
            :src="normalizeFile(file).thumbnailUrl"
            :alt="normalizeFile(file).name"
            class="size-full object-cover"
          />
          <!-- Icône pour les autres types -->
          <span v-else :class="getFileIcon(file)" class="size-5" />
        </div>

        <!-- Info du fichier -->
        <div class="flex-1 min-w-0">
          <p
            class="text-sm font-medium text-[#1D1D1F] dark:text-white truncate"
          >
            {{ normalizeFile(file).name }}
          </p>
          <p class="text-xs text-[#6B7280] dark:text-[#9CA3AF]">
            {{ formatFileSize(normalizeFile(file).size) }}
            <span
              v-if="!normalizeFile(file).isNew"
              class="ml-2 text-[10px] text-[#3860F9] dark:text-[#D3FD58]"
            >
              • {{ $t('TASKS.FILE_UPLOAD.SAVED') }}
            </span>
          </p>
        </div>

        <!-- Boutons d'action -->
        <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100">
          <!-- Bouton télécharger (uniquement pour les fichiers sauvegardés) -->
          <button
            v-if="!normalizeFile(file).isNew"
            type="button"
            class="size-8 rounded-lg flex items-center justify-center text-[#6B7280] dark:text-[#9CA3AF] hover:text-[#3860F9] dark:hover:text-[#D3FD58] hover:bg-[#3860F9]/10 dark:hover:bg-[#D3FD58]/10 transition-colors"
            @click="downloadFile(file)"
          >
            <span class="i-lucide-download size-4" />
          </button>

          <!-- Bouton supprimer -->
          <button
            type="button"
            class="size-8 rounded-lg flex items-center justify-center text-[#6B7280] dark:text-[#9CA3AF] hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            @click="removeFile(index)"
          >
            <span class="i-lucide-trash-2 size-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
